/**
 * FAQ Chat Widget
 *
 * Floating chat button for the public homepage.
 * Uses the FAQ agent for platform-only knowledge (no account access).
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageCircle, X, Send, Bot, User, Loader2, Sparkles, LogIn, Square } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

// FAQ Agent system prompt (embedded for public use)
const FAQ_SYSTEM_PROMPT = `You are the ManageClean OS Platform Expert, a helpful assistant on the public homepage.

You help visitors understand ManageClean OS, its features, pricing, and capabilities.

**WHAT YOU CAN HELP WITH:**
- Explaining ManageClean OS features and benefits
- Describing pricing plans and what's included
- Answering questions about the platform's capabilities
- Providing basic troubleshooting guidance

**WHAT YOU CANNOT DO:**
- Access any user account information
- Look up subscription status, billing, or payment details

**WHEN USERS ASK ABOUT THEIR ACCOUNT:**
Politely explain that for account-specific questions, they should sign in and visit the Support page.`;

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

const STORAGE_KEY = "manageclean_faq_thread";
const WELCOME_DELAY_MS = 30000; // 30 seconds
const TYPEWRITER_SPEED_MS = 15; // Characters per interval for typewriter effect

export function FAQChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Streaming/typewriter state
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [displayedContent, setDisplayedContent] = useState<string>("");
  const [showWelcome, setShowWelcome] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const welcomeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load thread from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setMessages(
          parsed.messages.map((m: any) => ({
            ...m,
            timestamp: new Date(m.timestamp),
          }))
        );
      } catch (e) {
        console.error("Failed to parse saved FAQ thread:", e);
      }
    }
  }, []);

  // Save thread to localStorage
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ messages }));
    }
  }, [messages]);

  // Proactive welcome message
  useEffect(() => {
    if (!isOpen && messages.length === 0) {
      welcomeTimeoutRef.current = setTimeout(() => {
        setShowWelcome(true);
      }, WELCOME_DELAY_MS);
    }

    return () => {
      if (welcomeTimeoutRef.current) {
        clearTimeout(welcomeTimeoutRef.current);
      }
    };
  }, [isOpen, messages.length]);

  // Scroll to bottom on new messages and during streaming
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, displayedContent]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setShowWelcome(false);

    // Add welcome message if first time
    if (messages.length === 0) {
      setMessages([
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content:
            "Hi! 👋 I'm the ManageClean OS Assistant. I can help you learn about our platform, features, and pricing. What would you like to know?",
          timestamp: new Date(),
        },
      ]);
    }
  }, [messages.length]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSend = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: "user",
      content: input.trim(),
      timestamp: new Date(),
    };

    // Create placeholder for assistant message (for typewriter effect)
    const assistantMessageId = crypto.randomUUID();

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);
    setStreamingMessageId(assistantMessageId);
    setDisplayedContent("");

    try {
      // Get the Convex site URL for HTTP endpoints
      const convexUrl = import.meta.env.VITE_CONVEX_URL;
      const siteUrl = convexUrl?.replace(".cloud", ".site") || "";

      // Call the real FAQ agent endpoint
      const response = await fetch(`${siteUrl}/faq-chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: userMessage.content,
          threadId: localStorage.getItem(STORAGE_KEY + "_threadId") || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Save thread ID for continuation
      if (data.threadId) {
        localStorage.setItem(STORAGE_KEY + "_threadId", data.threadId);
      }

      const fullContent = data.response || "I'm sorry, I couldn't generate a response.";

      // Add assistant message placeholder for streaming
      const assistantMessage: Message = {
        id: assistantMessageId,
        role: "assistant",
        content: fullContent,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Typewriter effect - progressively reveal text
      let charIndex = 0;
      const typeWriter = () => {
        if (charIndex < fullContent.length) {
          // Update displayed content progressively (faster for longer messages)
          const charsPerTick = fullContent.length > 500 ? 3 : fullContent.length > 200 ? 2 : 1;
          charIndex = Math.min(charIndex + charsPerTick, fullContent.length);
          setDisplayedContent(fullContent.slice(0, charIndex));
          setTimeout(typeWriter, TYPEWRITER_SPEED_MS);
        } else {
          // Streaming complete
          setStreamingMessageId(null);
          setDisplayedContent("");
        }
      };
      typeWriter();
    } catch (error) {
      console.error("FAQ chat error:", error);

      // Fallback to simulated response if the endpoint fails
      const fallbackResponse = await simulateFAQResponse(userMessage.content);
      const fullContent = fallbackResponse;

      const assistantMessage: Message = {
        id: assistantMessageId,
        role: "assistant",
        content: fullContent,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Typewriter effect for fallback too
      let charIndex = 0;
      const typeWriter = () => {
        if (charIndex < fullContent.length) {
          const charsPerTick = fullContent.length > 500 ? 3 : fullContent.length > 200 ? 2 : 1;
          charIndex = Math.min(charIndex + charsPerTick, fullContent.length);
          setDisplayedContent(fullContent.slice(0, charIndex));
          setTimeout(typeWriter, TYPEWRITER_SPEED_MS);
        } else {
          setStreamingMessageId(null);
          setDisplayedContent("");
        }
      };
      typeWriter();
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleStop = useCallback(() => {
    setIsLoading(false);
    setStreamingMessageId(null);
  }, []);

  const handleClearHistory = useCallback(() => {
    setMessages([
      {
        id: crypto.randomUUID(),
        role: "assistant",
        content:
          "Hi! 👋 I'm the ManageClean OS Assistant. I can help you learn about our platform, features, and pricing. What would you like to know?",
        timestamp: new Date(),
      },
    ]);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY + "_threadId");
  }, []);

  const isGenerating = isLoading || streamingMessageId !== null;

  return (
    <>
      {/* Floating Button */}
      <div className="fixed bottom-6 right-6 z-50">
        {/* Welcome bubble */}
        {showWelcome && !isOpen && (
          <div className="absolute bottom-16 right-0 mb-2 duration-300 animate-in fade-in slide-in-from-bottom-2">
            <Card className="max-w-xs border-blue-100 bg-white p-3 shadow-lg">
              <p className="text-sm text-slate-600">👋 Have questions about ManageClean OS?</p>
              <Button
                size="sm"
                variant="link"
                className="h-auto p-0 text-blue-600"
                onClick={handleOpen}
              >
                Chat with us →
              </Button>
            </Card>
          </div>
        )}

        {/* Main FAB */}
        <Button
          size="lg"
          className={cn(
            "flex h-14 w-14 items-center justify-center rounded-full p-0 shadow-lg transition-all duration-300",
            isOpen
              ? "bg-slate-800 text-white hover:bg-slate-700"
              : "bg-blue-600 text-white hover:bg-blue-700"
          )}
          onClick={isOpen ? handleClose : handleOpen}
        >
          {isOpen ? (
            <X className="h-6 w-6 text-white" />
          ) : (
            <MessageCircle className="h-6 w-6 text-white" />
          )}
        </Button>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 duration-200 animate-in fade-in slide-in-from-bottom-4">
          <Card className="flex h-[520px] w-[380px] flex-col overflow-hidden border-slate-200 shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b bg-gradient-to-r from-blue-600 to-indigo-600 p-4 text-white">
              <div className="flex items-center gap-2">
                <div className="rounded-lg bg-white/20 p-1.5">
                  <Sparkles className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold">Platform Assistant</h3>
                  <p className="text-xs text-blue-100">Ask about features & pricing</p>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 text-white hover:bg-white/20"
                onClick={handleClose}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={cn(
                      "flex gap-2",
                      message.role === "user" ? "justify-end" : "justify-start"
                    )}
                  >
                    {message.role === "assistant" && (
                      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                        <Bot className="h-4 w-4 text-blue-600" />
                      </div>
                    )}
                    <div
                      className={cn(
                        "max-w-[80%] rounded-2xl px-4 py-2 text-sm",
                        message.role === "user"
                          ? "rounded-br-md bg-blue-600 text-white"
                          : "rounded-bl-md bg-slate-100 text-slate-800"
                      )}
                    >
                      <div
                        className={
                          message.role === "assistant"
                            ? "prose prose-sm prose-slate max-w-none"
                            : "whitespace-pre-wrap"
                        }
                      >
                        {message.role === "assistant" ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {streamingMessageId === message.id ? displayedContent : message.content}
                          </ReactMarkdown>
                        ) : (
                          message.content
                        )}
                      </div>
                    </div>
                    {message.role === "user" && (
                      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-slate-200">
                        <User className="h-4 w-4 text-slate-600" />
                      </div>
                    )}
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start gap-2">
                    <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                      <Bot className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="flex items-center gap-2">
                      <svg
                        height={16}
                        viewBox="0 0 24 24"
                        width={16}
                        xmlns="http://www.w3.org/2000/svg"
                        className="text-blue-600"
                      >
                        <title>Loading...</title>
                        <style>{`
                                                    .spinner-bar-faq {
                                                        animation: spinner-bars-animation-faq .8s linear infinite;
                                                        animation-delay: -.8s;
                                                    }
                                                    .spinner-bars-faq-2 {
                                                        animation-delay: -.65s;
                                                    }
                                                    .spinner-bars-faq-3 {
                                                        animation-delay: -0.5s;
                                                    }
                                                    @keyframes spinner-bars-animation-faq {
                                                        0% {
                                                            y: 1px;
                                                            height: 22px;
                                                        }
                                                        93.75% {
                                                            y: 5px;
                                                            height: 14px;
                                                            opacity: 0.2;
                                                        }
                                                    }
                                                `}</style>
                        <rect
                          className="spinner-bar-faq"
                          fill="currentColor"
                          height="22"
                          width="6"
                          x="1"
                          y="1"
                        />
                        <rect
                          className="spinner-bar-faq spinner-bars-faq-2"
                          fill="currentColor"
                          height="22"
                          width="6"
                          x="9"
                          y="1"
                        />
                        <rect
                          className="spinner-bar-faq spinner-bars-faq-3"
                          fill="currentColor"
                          height="22"
                          width="6"
                          x="17"
                          y="1"
                        />
                      </svg>
                      <span className="text-sm text-slate-500">Thinking</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Sign in prompt */}
            <div className="border-b border-t bg-slate-50 px-4 py-2">
              <p className="flex items-center gap-1 text-xs text-slate-500">
                <LogIn className="h-3 w-3" />
                Need account help?{" "}
                <Link to="/sign-in" className="font-medium text-blue-600 hover:underline">
                  Sign in
                </Link>{" "}
                for support.
              </p>
            </div>

            {/* Input */}
            <div className="border-t bg-white p-3">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about ManageClean OS..."
                  className="flex-1 rounded-full border-slate-200 focus-visible:ring-blue-500"
                  disabled={isGenerating}
                />
                <Button
                  size="icon"
                  className={cn(
                    "h-10 w-10 rounded-full transition-colors",
                    isGenerating ? "bg-red-500 hover:bg-red-600" : "bg-blue-600 hover:bg-blue-700"
                  )}
                  onClick={isGenerating ? handleStop : handleSend}
                  disabled={!input.trim() && !isGenerating}
                >
                  {isGenerating ? (
                    <Square className="h-4 w-4 fill-current text-white" />
                  ) : (
                    <Send className="h-4 w-4 text-white" />
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}

// Temporary simulation until backend is connected
async function simulateFAQResponse(question: string): Promise<string> {
  await new Promise((resolve) => setTimeout(resolve, 1000));

  const q = question.toLowerCase();

  // Questions about pricing
  if (q.includes("price") || q.includes("cost") || q.includes("plan") || q.includes("how much")) {
    return "ManageClean OS offers flexible pricing plans:\n\n• **Free** - Basic features to get started\n• **Starter** ($29/mo) - For small businesses\n• **Professional** ($79/mo) - Advanced features\n• **Team** ($149/mo) - Full platform access\n\nAll plans include our AI assistant and core management tools. Would you like details on any specific plan?";
  }

  // Questions about how platform helps / benefits / janitorial business
  if (
    q.includes("help") ||
    q.includes("janitorial") ||
    q.includes("cleaning") ||
    q.includes("business") ||
    q.includes("benefit") ||
    q.includes("why")
  ) {
    return "ManageClean OS is specifically designed for janitorial and cleaning businesses! Here's how we help:\n\n🎯 **Win More Bids** - AI-powered bid creation with accurate pricing\n📅 **Streamline Scheduling** - Manage staff, routes, and client locations\n💼 **Grow Your Business** - Lead tracking and client management\n📝 **Professional Documents** - Generate contracts, proposals, and invoices\n🤖 **AI Assistant** - Your 24/7 business advisor\n\nWhether you're a solo cleaner or manage multiple crews, we scale with you. What aspect would you like to explore?";
  }

  // Questions about features
  if (
    q.includes("feature") ||
    q.includes("what can") ||
    q.includes("do") ||
    q.includes("capabilities")
  ) {
    return "ManageClean OS is a complete business management platform for cleaning companies:\n\n✨ **AI-Powered Assistant** - Your business second brain\n📊 **Lead & Client Management** - Track your pipeline\n📝 **Bid Management** - Create winning proposals\n👥 **Team Scheduling** - Manage staff & locations\n📄 **Document Generation** - AI-powered proposals & contracts\n📈 **Analytics** - Track performance and growth\n\nWould you like to know more about any specific feature?";
  }

  // Getting started questions
  if (q.includes("start") || q.includes("begin") || q.includes("sign up") || q.includes("try")) {
    return "Getting started with ManageClean OS is easy!\n\n1️⃣ **Sign up free** - No credit card required\n2️⃣ **Set up your company profile** - Takes just 5 minutes\n3️⃣ **Import or add your first clients** - We'll help organize them\n4️⃣ **Create your first bid** - Our AI makes it simple\n\nYou can try all features free, then upgrade when you're ready. Click 'Sign Up' in the top right to begin!";
  }

  // Account-specific questions (redirect to sign in)
  if (
    q.includes("account") ||
    q.includes("billing") ||
    q.includes("subscription") ||
    q.includes("my ")
  ) {
    return "For account-specific questions like billing, subscription status, or account settings, please sign in and visit our Support page at /dashboard/help/support. There I can access your account details and help you directly!\n\nIs there anything about the platform itself I can help you with?";
  }

  // Default response - more helpful
  return "Great question! ManageClean OS helps cleaning businesses like yours run more efficiently with AI-powered tools.\n\nI can tell you about:\n• 🎯 **How we help janitorial businesses** succeed\n• 💰 **Pricing plans** and what's included\n• ✨ **Key features** like bidding, scheduling, and more\n• 🚀 **Getting started** with a free account\n\nWhat interests you most?";
}

export default FAQChatWidget;
